import { Checkbox } from 'antd';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { BookNode } from '../../../../pages/lms/assignment-mgmt/CreateAssignment';
import TextWinds from '../../../common/TextWinds';
import {
  SUBJECT_COLOR_MAP,
  SUBJECT_LABEL_MAP,
} from '../../../../constants/types/lms/type';
import { useAssignmentPreview } from './hooks/useAssignmentPreview';

interface AssignmentSelectionWmwPanelProps {
  bookNodes: BookNode[];
  selectedAssignmentIds: Set<number>;
  openedBookIds: Set<string>;
  onBookOpen: (bookId: string) => void | Promise<void>;
  onBookSelect: (bookId: string) => void | Promise<void>;
  onAssignmentSelect: (assignmentId: number) => void;
}

const getBookAssignmentIds = (book: BookNode) => {
  return book.assignments?.map((assignment) => assignment.id) ?? [];
};

const getBookSelectionState = (
  book: BookNode,
  selectedAssignmentIds: Set<number>,
) => {
  const assignmentIds = getBookAssignmentIds(book);

  const hasSelectedAssignment = assignmentIds.some((id) =>
    selectedAssignmentIds.has(id),
  );

  const isAllAssignmentsSelected =
    assignmentIds.length > 0 &&
    assignmentIds.every((id) => selectedAssignmentIds.has(id));

  return {
    checked: book.isAssignmentLoaded && isAllAssignmentsSelected,
    indeterminate: !isAllAssignmentsSelected && hasSelectedAssignment,
  };
};

const AssignmentSelectionWmwPanel = ({
  bookNodes,
  selectedAssignmentIds,
  openedBookIds,
  onBookOpen,
  onBookSelect,
  onAssignmentSelect,
}: AssignmentSelectionWmwPanelProps) => {
  const { onPreviewClick } = useAssignmentPreview();

  return (
    <section className="flex h-[480px] flex-col rounded-xl bg-white p-4 shadow-card xl:h-[calc(100vh_-_180px)] xl:p-6">
      <div className="mb-5 flex shrink-0 items-start justify-between gap-4">
        <TextWinds type="title_h3">과제 선택</TextWinds>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="flex flex-col gap-3">
          {bookNodes.map((book) => {
            const bookSelectionState = getBookSelectionState(
              book,
              selectedAssignmentIds,
            );

            const isBookOpened = openedBookIds.has(book.id);

            return (
              <div
                key={book.id}
                className="overflow-hidden rounded-xl border border-gray3 bg-white transition-shadow hover:shadow-sm"
              >
                <div className="flex items-center justify-between gap-3 bg-gray1 px-4 py-3">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1 text-left xl:gap-3"
                    onClick={() => onBookOpen(book.id)}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-gray7 shadow-sm">
                      {isBookOpened ? (
                        <ChevronDownIcon className="h-5 w-5" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5" />
                      )}
                    </span>

                    <div
                      className={[
                        'flex h-12 w-12 shrink-0 items-center justify-center whitespace-pre-line rounded-xl text-center font-extrabold leading-tight',
                        SUBJECT_COLOR_MAP[book.type],
                      ].join(' ')}
                    >
                      {SUBJECT_LABEL_MAP[book.type]}
                    </div>

                    <div className="min-w-0">
                      <TextWinds type="content_body2" className="font-bold">
                        {book.title}
                      </TextWinds>
                    </div>
                  </button>

                  <Checkbox
                    checked={bookSelectionState.checked}
                    indeterminate={bookSelectionState.indeterminate}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onBookSelect(book.id)}
                  >
                    <TextWinds type="caption1" color="gray8">
                      전체 선택
                    </TextWinds>
                  </Checkbox>
                </div>

                {isBookOpened && book.isAssignmentLoaded && (
                  <div className="border-t border-gray2 bg-white px-4 py-3">
                    {book.assignments && book.assignments.length > 0 ? (
                      <div className="flex flex-col overflow-hidden rounded-lg border border-gray2 bg-gray0">
                        {book.assignments.map((assignment) => (
                          <label
                            key={assignment.id}
                            className="flex cursor-pointer items-center gap-3 border-b border-gray2 px-3 py-2.5 last:border-b-0 hover:bg-white"
                          >
                            <Checkbox
                              checked={selectedAssignmentIds.has(assignment.id)}
                              onChange={() => onAssignmentSelect(assignment.id)}
                            />

                            <div className="min-w-0 flex-1">
                              <TextWinds
                                type="caption1"
                                className="block truncate"
                              >
                                {assignment.title}
                              </TextWinds>
                            </div>

                            <button
                              type="button"
                              className="ml-auto flex shrink-0 items-center justify-center gap-1.5 rounded-lg border !border-purple5 bg-white px-2.5 py-1 text-xs font-bold text-purple5 hover:!border-purple6 hover:!bg-purple1 hover:text-purple5"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                onPreviewClick(assignment);
                              }}
                            >
                              미리보기
                            </button>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="flex h-[72px] items-center justify-center rounded-lg border border-dashed border-gray3 bg-gray0 px-4 text-center">
                        <TextWinds type="content_body3" color="gray6">
                          등록 가능한 과제가 없습니다.
                        </TextWinds>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AssignmentSelectionWmwPanel;
